// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useMemo } from 'react'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { formatNpi, formatDea, isSyntheticNpi, formatPhone } from '../../api/format'
import { ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import { useColumnManager } from '../../components/ColumnManager'
import { ColumnFilterButton, ColumnFilterChips, funnelToServerOps, type ColumnFilters } from '../../components/common/ColumnFilter'
import { useAuth } from '../../auth/AuthContext'
import { listScrollStyle, stickyHeaderStyle } from '../../styles/listScroll'

// D-VIEWCOLS-1: def-driven table. Column set/order/labels come from the pharmacy's
// admin config (prescriber_list companion surface); on top of that each USER can
// hide/unhide + reorder via the Columns manager and click headers to sort.
type ColDef = {
  key: string
  label: string
  render: (p: api.PrescriberListItem) => React.ReactNode
  sortValue?: (p: api.PrescriberListItem) => string | number
}

export default function PrescriberListPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<api.PrescriberListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  // Search is a SERVER predicate now: each keystroke is a filtered query plus the dedupe pass.
  // 250ms (sized when filtering happened in-browser) meant several round trips per typed word.
  const debouncedQ = useDebouncedValue(q, 1000)
  // Honor the pharmacy's prescriber-header customization (hide fields removed there).
  const [layout, setLayout] = useState<api.RxViewLayout | null>(null)
  // RCA-CV-5 L7 (E-07): a failed layout read falls back to defaults — but says so, instead of
  // making "the portal shows defaults" indistinguishable from "nothing configured".
  const [layoutIssue, setLayoutIssue] = useState<string | null>(null)
  useEffect(() => {
    api.getRxViewLayout(undefined, 'prescriber').then(setLayout)
      .catch(() => setLayoutIssue('The configured prescriber layout could not be loaded; showing the default columns.'))
  }, [])
  const hide = useMemo(() => (col: string) => api.headerFieldHidden(layout, col), [layout])
  const npiColHidden = useMemo(() => hide('npi') && hide('dea_number'), [hide])

  // P2 (e2e pagination) hybrid, same shape as PatientListPage: server-page the DEFAULT view
  // (no header sort, no column filter) so the browser pulls one page instead of every
  // prescriber; any sort/filter falls back to the full fetch + the existing client pipeline
  // (the round-table red line: never server-page while filter/sort is client-side). Search is
  // pushed server-side in server mode (the endpoint's q matches name/NPI/specialty/phone —
  // the same fields the client filter matches).
  // D-RXFILTER-3: sort and column filters are SERVER predicates. There is no client fallback, so
  // "next page" is always a network call and the total always counts every merged prescriber.
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null)
  const [colFilters, setColFilters] = useState<ColumnFilters>({})

  // Capability comes from the server registry — never a second list here.
  const [caps, setCaps] = useState<Map<string, api.RxColumnCap>>(new Map())
  const [capsLoaded, setCapsLoaded] = useState(false)
  useEffect(() => {
    api.getPrescriberColumnCaps()
      .then(r => setCaps(new Map(r.items.map(c => [c.key, c]))))
      .catch(() => { /* degrade to funnels disabled; never block the list */ })
      .finally(() => setCapsLoaded(true))
  }, [])

  // Display units are not all 1:1 with registry columns; this is the one place that maps them.
  const REGISTRY_KEY: Record<string, string> = {
    prescriber: 'display_name',
    contact: 'primary_phone',
    clinic: 'clinic_name',
    rx: 'rx_total',
    // The column's key is last_activity. This map said last_rx, a key no column has, so Last
    // activity never found its capability and showed "cannot be filtered" over a date the
    // server has always taken bounds for.
    last_activity: 'last_rx_at',
    npi_dea: 'npi_dea',
  }
  const regKey = (uiKey: string) => REGISTRY_KEY[uiKey] ?? uiKey

  const { prFilters, blockedCols } = useMemo(() => {
    const pushed: api.RxFilter[] = []
    const stuck: string[] = []
    for (const [uiKey, set] of Object.entries(colFilters)) {
      if (!set || set.size === 0) continue
      const key = regKey(uiKey)
      const cap = caps.get(key)
      if (!cap?.filterable) { stuck.push(uiKey); continue }
      // One translation for every server-paged list (D-UI-2): value lists, from/to bounds and
      // the contains box all go through it, against the operators the registry admits.
      const t = funnelToServerOps(set, cap.ops)
      for (const p of t.preds) pushed.push({ key, ...p })
      if (t.blocked) stuck.push(uiKey)
    }
    return { prFilters: pushed, blockedCols: stuck }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colFilters, caps])

  const sortKeySrv = sort && caps.get(regKey(sort.key))?.sortable ? regKey(sort.key) : undefined
  const filterSig = api.encodeRxFilters(prFilters).join('|')
  const [srvPage, setSrvPage] = useState(1)
  const [srvPageSize, setSrvPageSize] = useState(() => preferredPageSize())
  const [srvTotal, setSrvTotal] = useState<number | null>(null)
  const [srvTotalPages, setSrvTotalPages] = useState<number | null>(null)
  useEffect(() => { setSrvPage(1) }, [debouncedQ, srvPageSize, filterSig, sortKeySrv, sort?.dir])

  useEffect(() => {
    // ONE request shape. The old hybrid fetched every prescriber the moment a filter or sort was
    // active and paged them in the browser, so the page buttons did nothing over the network and
    // the funnels offered only the values already on screen.
    //
    // Hold only when something actually depends on capabilities: a cold default view must not wait
    // on a second request before showing its first page.
    if (!capsLoaded && (Object.keys(colFilters).length > 0 || sort)) return
    setLoading(true); setError(null)
    api.listPrescribersPaged({
      page: srvPage, pageSize: srvPageSize, q: debouncedQ || undefined,
      orderBy: sortKeySrv, orderDir: sortKeySrv ? (sort!.dir === 1 ? 'asc' : 'desc') : undefined,
      filters: prFilters,
    })
      .then(r => {
        setItems(r.items)
        setSrvTotal(typeof r.total === 'number' ? r.total : null)
        setSrvTotalPages(typeof r.total_pages === 'number' ? r.total_pages : null)
      })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srvPage, srvPageSize, debouncedQ, filterSig, sortKeySrv, sort?.dir, capsLoaded])

  // ---- Admin layer: pharmacy home-table column config (prescriber_list surface). ----
  const [listCfg, setListCfg] = useState<api.RxViewLayout | null>(null)
  useEffect(() => {
    api.getRxViewLayout(undefined, 'prescriber_list').then(setListCfg)
      .catch(() => setLayoutIssue('The configured column layout could not be loaded; showing the default columns.'))
  }, [])
  const cfgFields = useMemo(() => (((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.fields ?? []) as any[]), [listCfg])
  // D-VIEWCOLS-2b: per-column sub-text (second line) suppression.
  const subHidden = useMemo(() => new Set(((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.config as any)?.subtext_hidden ?? []) as string[])), [listCfg])

  // Custom field values, batch-resolved for the visible page (same flow as the Prescriptions queue).
  const [cfValues, setCfValues] = useState<Record<string, Record<string, string | null>>>({})
  const cfVal = (id: string, col: string) => {
    const row = cfValues[id]; if (!row) return '—'
    const k = Object.keys(row).find(x => x.endsWith(`.${col}`))
    return (k && row[k]) || '—'
  }

  // Standard column units. RBAC field rules still apply inside and to whole units.
  const baseDefs: ColDef[] = useMemo(() => {
    const units: ColDef[] = [
      {
        key: 'prescriber', label: 'Prescriber',
        sortValue: p => (p.display_name ?? '').toLowerCase(),
        render: p => (
          <>
            <div style={{ fontWeight: 500 }}>{p.display_name}</div>
            {!hide('suffix') && !subHidden.has('prescriber') && p.suffix && <div style={mutedSm}>{p.suffix}</div>}
          </>
        ),
      },
      ...(!npiColHidden ? [{
        key: 'npi_dea', label: 'NPI / DEA',
        sortValue: (p: api.PrescriberListItem) => p.npi ?? '',
        render: (p: api.PrescriberListItem) => (
          <>
            {!hide('npi') && <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatNpi(p.npi)}</div>}
            {!hide('dea_number') && !subHidden.has('npi_dea') && p.dea_number && !isSyntheticNpi(p.dea_number) && (
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-mute)' }}>{formatDea(p.dea_number)}</div>
            )}
          </>
        ),
      }] : []),
      ...(!hide('specialty') ? [{
        key: 'specialty', label: 'Specialty',
        sortValue: (p: api.PrescriberListItem) => (p.specialty ?? '').toLowerCase(),
        render: (p: api.PrescriberListItem) => (
          <>
            <div>{p.specialty ?? '—'}</div>
            {!subHidden.has('specialty') && <div style={mutedSm}>{p.prescriber_type ?? ''}</div>}
          </>
        ),
      }] : []),
      {
        key: 'clinic', label: 'Clinic',
        sortValue: p => (p.clinic_name ?? '').toLowerCase(),
        render: p => (
          <>
            <div>{p.clinic_name ?? '—'}</div>
            {!subHidden.has('clinic') && <div style={mutedSm}>{[p.city, p.state_code].filter(Boolean).join(', ')}</div>}
          </>
        ),
      },
      {
        key: 'contact', label: 'Contact',
        sortValue: p => (p.primary_phone ?? p.email ?? '').toLowerCase(),
        render: p => (
          <>
            <div>{formatPhone(p.primary_phone)}</div>
            {!subHidden.has('contact') && <div className="email-text" style={mutedSm}>{p.email ?? ''}</div>}
          </>
        ),
      },
      {
        key: 'rx', label: 'Rx written',
        sortValue: p => p.rx_total ?? 0,
        render: p => <strong>{p.rx_total}</strong>,
      },
      {
        key: 'last_activity', label: 'Last activity',
        sortValue: p => p.last_rx_at ?? '',
        render: p => <>{p.last_rx_at ? relativeTime(p.last_rx_at) : '—'}</>,
      },
    ]
    if (!cfgFields.length) return units
    const byKey = new Map(units.map(d => [d.key, d]))
    const out: ColDef[] = []
    for (const f of cfgFields) {
      if (!f.visible) continue
      if (f.owlrx_table === 'custom') {
        out.push({
          key: `cf:${f.owlrx_column}`,
          label: (f.owlrx_label || '').trim() || f.owlrx_column,
          render: p => <>{cfVal(p.id, f.owlrx_column)}</>,
        })
      } else {
        const d = byKey.get(f.owlrx_column)
        if (d) out.push({ ...d, label: (f.owlrx_label || '').trim() || d.label })
      }
    }
    // Units the admin config predates (added later in code) stay visible by default.
    const seen = new Set(out.map(d => d.key))
    const mentioned = new Set(cfgFields.map((f: any) => f.owlrx_table === 'custom' ? `cf:${f.owlrx_column}` : f.owlrx_column))
    out.push(...units.filter(d => !seen.has(d.key) && !mentioned.has(d.key)))
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgFields, hide, npiColHidden, cfValues, subHidden])

  // ---- User layer: per-user hide/unhide + reorder, persisted per browser. ----
  const allLabels = useMemo(() => baseDefs.map(d => d.label), [baseDefs])
  const { visibleColumns: visLabels, managerUI, toggleButton } = useColumnManager('prescribers_list', allLabels)
  // D-VIEWCOLS-2a: columns the pharmacy admin hid from the provider view.
  const { me } = useAuth()
  const isProvider = me?.role_name === 'provider' || me?.role_name === 'provider_nurse'
  const provHidden = useMemo(() => new Set(((((listCfg?.cards as any[] | undefined)?.find(c => c.card_key === 'list_columns')?.config as any)?.provider_hidden ?? []) as string[])), [listCfg])
  const cols = useMemo(() => {
    const byLabel = new Map(baseDefs.map(d => [d.label, d]))
    return visLabels.map(l => byLabel.get(l))
      .filter((d): d is ColDef => !!d)
      .filter(d => !isProvider || !provHidden.has(d.key))
  }, [baseDefs, visLabels, isProvider, provHidden])

  // ---- Sort (header click), applied before pagination. State declared above (mode switch). ----
  const toggleSort = (key: string) => setSort(s => (s?.key === key ? (s.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 }))

  // The client filter pipeline is GONE. Rows arrive filtered, sorted and paged by the server, so
  // re-deriving here would filter a page and report it as a set — and funnel options now come from
  // /prescribers/facets rather than from whatever happens to be loaded.

  const pageRows = items
  const page = srvPage
  const setPage = setSrvPage
  const pageSize = srvPageSize
  const setPageSize = (n: number) => { rememberPageSize(n); setSrvPageSize(n) }
  const totalPages = srvTotalPages ?? 1
  const total = srvTotal ?? items.length

  const customCols = useMemo(() => cfgFields.filter((x: any) => x.owlrx_table === 'custom' && x.visible), [cfgFields])
  useEffect(() => {
    if (customCols.length === 0 || pageRows.length === 0) return
    let dead = false
    api.batchCustomFieldValues('prescriber', pageRows.map(p => p.id))
      .then(r => { if (!dead) setCfValues(v => ({ ...v, ...(r.values ?? {}) })) }).catch(() => {})
    return () => { dead = true }
  }, [customCols.length, pageRows])

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <h1 style={titleStyle}>Prescribers</h1>
        {/* In server mode items.length is ONE PAGE — show the true merged total. */}
        <div style={subtitleStyle}>{total.toLocaleString()} {total === 1 ? 'prescriber' : 'prescribers'}</div>
      </header>

      <div style={{ ...searchRow, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ ...searchWrap, flex: 1 }}>
          <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-mute)' }} />
          <input
            type="text"
            placeholder="Search by name, NPI, or specialty…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={searchInput}
          />
        </div>
        {toggleButton}
      </div>
      {managerUI}

      {layoutIssue && <ErrorBanner message={layoutIssue} onClose={() => setLayoutIssue(null)} />}
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div style={cardStyle}>
        {loading ? (
          <LoadingState label="Loading prescribers…" />
        ) : pageRows.length === 0 ? (
          <EmptyState message={q ? `No prescribers match "${q}".` : 'No prescribers found.'} />
        ) : (
          <>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="prescribers" />
          <ColumnFilterChips filters={colFilters} labels={Object.fromEntries(baseDefs.map(d => [d.key, d.label]))} onClear={k => setColFilters(f => ({ ...f, [k]: new Set() }))} onClearAll={() => setColFilters({})} />
          {blockedCols.length > 0 && (
            <div style={{ fontSize: 12.5, padding: '7px 10px', marginBottom: 8, borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f' }}>
              {/* A filter the server has no expression for is refused and NAMED — never silently
                  ignored, and never widened into a full-table fetch. */}
              <b>{blockedCols.map(k => baseDefs.find(d => d.key === k)?.label ?? k).join(', ')}</b>
              {blockedCols.length > 1 ? ' cannot be filtered' : ' cannot be filtered'} on the server, so
              {blockedCols.length > 1 ? ' they are' : ' it is'} not applied. Everything else below is filtered across all {total.toLocaleString()} prescribers.
            </div>
          )}
          <div style={listScrollStyle(300)}>
      <table style={tableStyle}>
            <thead>
              <tr>
                {cols.map(c => (
                  <th
                    key={c.key}
                    style={{ ...thStyle, cursor: c.sortValue ? 'pointer' : 'default', userSelect: 'none' }}
                    onClick={c.sortValue ? () => toggleSort(c.key) : undefined}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}
                  >
                    {c.label}{sort?.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}{' '}
                    {(() => {
                      // Server-backed options, or none at all. A loaded-rows funnel here would list
                      // the specialties on THIS page and hide the rest. Prescriber names and NPIs
                      // are refused by the registry on purpose: searchable, never enumerable.
                      const rk = regKey(c.key)
                      const cap = caps.get(rk)
                      const isDate = cap?.klass === 'date'
                      // D-RXFILTER-4 added the numeric range control to the shared funnel. This
                      // list's rx_total already accepted gte/lte at the API and had no way to
                      // reach it — the capability existed but the header could not offer it.
                      const isNumeric = cap?.klass === 'numeric'
                      // D-UI-2: a column the registry filters by CONTAINS and never lists (a name,
                      // an MRN, a phone number) gets the funnel with a text box in it.
                      const isText = !!cap?.filterable && !cap.facetable && !isDate && !isNumeric && cap.ops.includes('contains')
                      if (!cap?.facetable && !isDate && !isNumeric && !isText) return (
                        <span title={cap?.reason ?? `${c.label} cannot be filtered on the server.`}
                          aria-label={`${c.label} filter unavailable`}
                          style={{ display: 'inline-block', width: 14, opacity: 0.25, cursor: 'not-allowed', fontSize: 11 }}>⌀</span>
                      )
                      return (
                    <ColumnFilterButton label={c.label}
                      isDate={isDate}
                      isNumeric={isNumeric}
                      isText={isText}
                      fetchOptions={cap?.facetable ? (term: string) => api.getPrescriberFacet({
                        column: rk, q: term,
                        filters: prFilters.filter(f => f.key !== rk),
                      }) : undefined}
                      selected={colFilters[c.key] ?? new Set()}
                      onChange={next => { setColFilters(f => ({ ...f, [c.key]: next })); setPage(1) }} />
                      ) })()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map(p => (
                <tr
                  key={p.id}
                  style={trStyle}
                  onClick={() => navigate(`/prescribers/${p.id}`)}
                  onKeyDown={e => { if (e.key === 'Enter') navigate(`/prescribers/${p.id}`) }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open prescriber ${p.display_name}`}
                >
                  {cols.map(c => <td key={c.key} style={tdStyle}>{c.render(p)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="prescribers" />
          </>
        )}
      </div>
    </div>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const headerStyle: React.CSSProperties = { marginBottom: 16 }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 4, color: 'var(--color-text-soft)', fontSize: 13 }
const searchRow: React.CSSProperties = { marginBottom: 16 }
const searchWrap: React.CSSProperties = { position: 'relative', maxWidth: 400 }
const searchInput: React.CSSProperties = {
  width: '100%', padding: '8px 12px 8px 32px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontFamily: 'inherit', boxSizing: 'border-box',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden',
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }
const thStyle: React.CSSProperties = {
  ...stickyHeaderStyle,
  textAlign: 'left', padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase', color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }
const tdStyle: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'middle' }
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
